/*
	This form is for Flashtalking Facebook connections.  For Mediaocean Facebook connections, see FacebookImageMO
*/

import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Field, change, FormSection } from "redux-form";

import { ReduxControls, ProgressIndicator } from "rhombus-react";
import AssetSelect from "social/AssetSelect";

import { noop } from "utils/common";
import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";

import { loadFacebookPages } from "reducers/Social";

import {
	mapStringToOption,
	normalizeOptionToString,
	formatAPIOption,
	normalizeAPIOption,
	mapFacebookStringToOption
} from "social/utils";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { FACEBOOK_IMAGE_CONSTRAINTS } from "social/validators/facebook";
import PACController from "social/PACController";
import { PAC_FORM_TYPES } from "social/constants";

import { buildFacebookPages } from "selectors/Social";
import { tellSentry } from "utils/Rave";
import { FB_CTA_OPTIONS } from "social/constants/CTA";

const {
	MAX_LENGTH_LANDING_PAGE,
	MAX_IMAGE_MB,
	IMAGE_ACCEPT,
} = FACEBOOK_IMAGE_CONSTRAINTS;

const FILE_SIZE_REQUIREMENT = {
	name: "File Size:",
	description: `${MAX_IMAGE_MB}MB Maximum`,
};

const FILE_TYPE_REQUIREMENT = {
	name: "File Types:",
	description: IMAGE_ACCEPT,
};

const FACEBOOK_IMAGE_REQUIREMENTS = {
	[PAC_FORM_TYPES.DEFAULT] : [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1080 or larger recommended" }
	],
	[PAC_FORM_TYPES.HORIZONTAL] : [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x566 or larger recommended" }
	],
	[PAC_FORM_TYPES.VERTICAL]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1920 or larger recommended" }
	],
	[PAC_FORM_TYPES.SQUARE]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1080 or larger recommended" }
	],
	[PAC_FORM_TYPES.REELS]: [
		FILE_SIZE_REQUIREMENT,
		FILE_TYPE_REQUIREMENT,
		{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1920 or larger recommended" }
	],
};

function getFacebookImageFields(placementType) {
	return {
		image: {
			name: "image",
			label: "Image",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: AssetSelect,
			requirements: FACEBOOK_IMAGE_REQUIREMENTS[placementType],
			required: true,
			accept: IMAGE_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 600x600 for better placement compatibility",
			},
		},
		name: {
			name: "name",
			label: "Creative Name",
			className: "full-width",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
			required: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			className: "full-width",
			placeholder: "Enter a short headline",
			tip: {
				description: "May get truncated after 40 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
		},
		primaryText: {
			name: "primaryText",
			label: "Primary Text",
			className: "full-width",
			placeholder: "Tell people what your creative is about",
			tip: {
				description: "May get truncated after 125 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
		},
		description: {
			name: "description",
			label: "Description",
			className: "full-width",
			placeholder: "Include additional details",
			tip: {
				description: "May get truncated after 30 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			className: "full-width",
			placeholder: "https://www.example.com",
			component: ReduxControls.Input,
			charMax: MAX_LENGTH_LANDING_PAGE,
			showCharCount: true,
			required: true,
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: ReduxControls.Input,
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Enter Call to Action Text",
			component: ReduxControls.Select,
			options: FB_CTA_OPTIONS,
			normalize: normalizeOptionToString,
			format: mapStringToOption,
			isClearable: false,
			menuPlacement: "top",
			required: true,
		},
		facebookPage: {
			name: "facebookPage",
			label: "Facebook Page",
			className: "half-width",
			component: ReduxControls.Select,
			required: true,
			normalize: normalizeAPIOption,
			format: formatAPIOption,
			placeholder: "Select...",
		},
		instagramAccount: {
			name: "instagramAccount",
			label: "Instagram Account",
			className: "half-width",
			placeholder: "Select...",
			component: ReduxControls.Select,
			normalize: normalizeAPIOption,
			format: formatAPIOption,
		},
	};
}

function fieldGenerator(formProps, customGenerator) {
	/*
		Since placement forms utilize the same fields as the default form (for now). The adType can dictate
		how fieldProps are generated. This field generator returns a function for use inside a map on an array
		of fieldNames.

		We can also pass in a custom generator to build placement specific props, if that case ever occurs.

		This function is passed to the PACController so it can generate fields for the individual placement
		forms.
	*/
	const {
		navigationActions,
		libraryId,
		isViewOnly,
		showPristineErrors,
		placementType,
		facebookPages=[],
		instagramAccounts=[],
	} = formProps;

	const facebookPagesTextProps = {
		component: ReduxControls.Input,
		normalize: mapFacebookStringToOption,
		format: normalizeOptionToString,
		placeholder: "Enter Facebook Page id",
	};
	const instagramTextProps = {
		component: ReduxControls.Input,
		normalize: mapFacebookStringToOption,
		format: normalizeOptionToString,
		placeholder: "Enter Instagram account id",
	};

	const facebookImageFields = useMemo(() => {
		return getFacebookImageFields(placementType);
	}, [placementType]);

	return (name) => {
		const fieldProps = facebookImageFields[name];

		if (!fieldProps) { // Sanity check
			console.warn(`Unknown field name in FacebookImage: ${name}`);
			tellSentry(`Unknown field name in FacebookImage: ${name}`);
			return null;
		}

		let extraProps = {};
		let customProps = {};
		if (customGenerator) {
			customProps = customGenerator(formProps);
		}

		if(fieldProps.name === "image") {
			extraProps.navigationActions = navigationActions;
			extraProps.libraryId = libraryId;
		}

		if(fieldProps.name === "facebookPage"){
			if(!facebookPages.length) {
				extraProps = {
					...extraProps,
					...facebookPagesTextProps,
				};
			} else {
				extraProps.options = facebookPages;
				extraProps.menuPlacement = "top";
				// We only want to add `onMenuScrollToBottom` for the Select input
				extraProps.onMenuScrollToBottom = noop;
			}
		}

		if(fieldProps.name === "instagramAccount"){
			if(!instagramAccounts.length){
				extraProps = {
					...extraProps,
					...instagramTextProps,
				};
			} else {
				extraProps.options = instagramAccounts;
				extraProps.menuPlacement = "top";
				// We only want to add `onMenuScrollToBottom` for the Select input
				extraProps.onMenuScrollToBottom = noop;
			}
		}

		extraProps.disabled = isViewOnly;

		if (extraProps.disabled && fieldProps.placeholder) {
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = '';
		}

		extraProps.showPristineErrors = showPristineErrors;

		return	(
			<Field
				key={`${placementType}-${fieldProps.name}`}
				{...fieldProps}
				{...extraProps}
				{...customProps}
			/>
		);
	};
}

const adType = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;

function FacebookImageDefault(props) {
	const { isEditMode, libraryId } = props;
	const dispatch = useDispatch();

	const isLoading = useSelector(state => state.social?.facebookPages?.isLoading);
	const { facebookPages, instagramAccounts } = useSelector(buildFacebookPages);
	const { formValues, meta } = useSelector((state) => {
		let socialWizard = state.form?.[SOCIAL_WIZARD];
		return {
			formValues: socialWizard?.values,
			meta: socialWizard?.fields,
		};
	});

	useEffect(() => {
		dispatch(loadFacebookPages(libraryId));
	}, [libraryId]);

	useEffect(() => {
		if (isEditMode) { return; }

		const selectedFacebookPage = formValues?.[adType]?.facebookPage;
		const isTouchedFacebookPage = meta?.[adType]?.facebookPage?.touched;

		const selectedInstagramAccount = formValues?.[adType]?.instagramAccount;
		const isTouchedInstagramAccount = meta?.[adType]?.instagramAccount?.touched;

		if(!selectedFacebookPage && !isTouchedFacebookPage){
			dispatch(change(SOCIAL_WIZARD, `${adType}.facebookPage`, facebookPages?.[0]));
		}
		if(!selectedInstagramAccount && !isTouchedInstagramAccount) {
			dispatch(change(SOCIAL_WIZARD, `${adType}.instagramAccount`, instagramAccounts?.[0]));
		}
	}, [facebookPages, instagramAccounts]);

	const generateFields = fieldGenerator({
		...props,
		placementType: PAC_FORM_TYPES.DEFAULT,
		facebookPages,
		instagramAccounts,
	});

	const fields = useMemo(() => {
		return Object.keys(getFacebookImageFields(PAC_FORM_TYPES.DEFAULT)).map(generateFields);
	}, [facebookPages, instagramAccounts]);

	return (
		<>
			<ProgressIndicator isLoading={isLoading} />
			<div className="fields">
				{fields}
			</div>
		</>
	);
}

FacebookImageDefault.displayName = "FacebookImageDefaultForm";

export default function FacebookImage(props){
	const defaultForm = <FacebookImageDefault {...props} />;

	return (
		<FormSection name={adType}>
			<PACController
				adType={adType}
				defaultForm={defaultForm}
				defaultFieldGenerator={fieldGenerator}
				{...props}
			/>
		</FormSection>
	);
}

FacebookImage.displayName = "FacebookImageForm";

export const PRIVATE = {
	FacebookImageDefault,
};
